import { useState } from "react";
import { useLocation } from "wouter";

import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";

import { useRemontaLaudos, useRTsByEmpresa } from "@/hooks/use-data";
import { Shell } from "@/components/layout/Shell";

import { LAUDO_STATUS_LABELS, LAUDO_STATUS_COLORS, VEICULO_TIPO_LABELS, VEICULO_TIPO_COLORS, CONCLUSAO_LABELS, CONCLUSAO_COLORS } from "@/lib/constants";
import { formatCurrency } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

import { FileText, Plus, Car, User, Download, Building2 } from "lucide-react";

// ── Laudos page ──────────────────────────────────────────────────────────────

export default function Laudos() {
  const [, navigate] = useLocation();
  const { toast } = useToast();

  const [filters, setFilters] = useState({
    status: "",
    rtId: "",
    tipoVeiculo: "",
    placa: "",
  });

  const { profile } = useAuth();
  const roles = profile?.roles || [];
  const canViewAuditoria =
    roles.includes("auditor") || roles.includes("admin") || roles.includes("owner");
  const vistoriadoraId =
    (profile?.empresa as any)?.vistoriadoraId ?? profile?.empresaId;
  const isRequester = profile?.empresa?.tipo === "PARCEIRO_DE_NEGOCIOS";
  const { data: rtUsers = [] } = useRTsByEmpresa(isRequester ? undefined : vistoriadoraId);
  const { data: laudos = [], isLoading } = useRemontaLaudos({
    status: filters.status || undefined,
    rtId: filters.rtId || undefined,
    tipoVeiculo: filters.tipoVeiculo || undefined,
  });
  const rtOptions = isRequester
    ? Array.from(new Map(laudos.filter((laudo: any) => laudo.rt?.id).map((laudo: any) => [laudo.rt.id, laudo.rt])).values())
    : rtUsers;

  const anyFilterActive =
    !!filters.status ||
    !!filters.rtId ||
    !!filters.tipoVeiculo ||
    !!filters.placa;

  const visibleLaudos = filters.placa
    ? laudos.filter((l: any) =>
        l.veiculo?.placa?.toLowerCase().includes(filters.placa.toLowerCase()),
      )
    : laudos;

  return (
    <Shell>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div><h1 className="text-2xl font-bold">Laudos</h1><p className="mt-1 text-sm text-slate-600">Inspeções de Reclassificação e registros históricos. Os quatro serviços são contratados pelo Dossiê.</p></div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => navigate("/dossies")}>
              <Plus className="h-4 w-4 mr-2" />Nova Reclassificação
            </Button>
          </div>
        </div>

        {/* Filter bar */}
        <Card>
          <CardContent className="py-3">
            <div className="flex flex-wrap gap-3 items-end">
              <div className="w-44">
                <Label className="text-xs mb-1 block">Status</Label>
                <Select
                  value={filters.status}
                  onValueChange={(v) =>
                    setFilters((f) => ({ ...f, status: v === "_all" ? "" : v }))
                  }
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">Todos</SelectItem>
                    {Object.entries(LAUDO_STATUS_LABELS)
                      .filter(([k]) => k !== "em_auditoria" || canViewAuditoria)
                      .map(([k, v]) => (
                        <SelectItem key={k} value={k}>
                          {v}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="w-44">
                <Label className="text-xs mb-1 block">Tipo de Veículo</Label>
                <Select
                  value={filters.tipoVeiculo}
                  onValueChange={(v) =>
                    setFilters((f) => ({
                      ...f,
                      tipoVeiculo: v === "_all" ? "" : v,
                    }))
                  }
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">Todos</SelectItem>
                    {Object.entries(VEICULO_TIPO_LABELS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="w-44">
                <Label className="text-xs mb-1 block">RT</Label>
                <Select
                  value={filters.rtId}
                  onValueChange={(v) =>
                    setFilters((f) => ({ ...f, rtId: v === "_all" ? "" : v }))
                  }
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">Todos</SelectItem>
                    {rtOptions.map((rt: any) => (
                      <SelectItem key={rt.id} value={rt.id}>
                        {rt.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="w-44">
                <Label className="text-xs mb-1 block">Placa</Label>
                <Input
                  className="h-8 text-sm"
                  placeholder="Filtrar por placa"
                  value={filters.placa}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, placa: e.target.value }))
                  }
                />
              </div>

              {anyFilterActive && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={() =>
                    setFilters({
                      status: "",
                      rtId: "",
                      tipoVeiculo: "",
                      placa: "",
                    })
                  }
                >
                  Limpar filtros
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Laudo list */}
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-20 w-full rounded-lg" />
            ))}
          </div>
        ) : visibleLaudos.length === 0 ? (
          <div className="text-center py-16 text-slate-500">
            {anyFilterActive ? (
              <p>
                Nenhum laudo corresponde aos filtros selecionados. Tente ajustar
                os filtros.
              </p>
            ) : (
              <>
                <FileText className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                <p className="font-medium">Nenhum laudo encontrado</p>
                <p className="text-sm mt-1">
                  Crie o primeiro Laudo de Recuperabilidade selecionando um
                  veículo.
                </p>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {visibleLaudos.map((laudo: any) => (
              <Card
                key={laudo.id}
                className="cursor-pointer hover:border-primary transition-colors"
                onClick={() => navigate(`/laudos/${laudo.id}`)}
              >
                <CardContent className="py-4 px-5 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                    {laudo.codigo && (
                      <span className="font-mono text-xs text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                        {laudo.codigo}
                      </span>
                    )}
                    <span className="font-semibold text-sm">
                      {laudo.veiculo?.placa ?? `Laudo #${laudo.id.slice(0, 8)}`}
                    </span>
                    <Badge
                      className={`text-xs ${LAUDO_STATUS_COLORS[laudo.status] ?? "bg-slate-100 text-slate-700"}`}
                    >
                      {LAUDO_STATUS_LABELS[laudo.status] ?? laudo.status}
                    </Badge>
                    {laudo.veiculo?.tipoVeiculo && (
                      <Badge
                        className={`text-xs ${VEICULO_TIPO_COLORS[laudo.veiculo.tipoVeiculo] ?? ""}`}
                      >
                        {VEICULO_TIPO_LABELS[laudo.veiculo.tipoVeiculo] ??
                          laudo.veiculo.tipoVeiculo}
                      </Badge>
                    )}
                    {laudo.tipo !== "avaliacao" && (
                      <Badge variant="secondary">Reclassificação</Badge>
                    )}
                    <span className="ml-auto text-xs text-slate-400">
                      {laudo.criadoEm
                        ? new Date(laudo.criadoEm).toLocaleDateString("pt-BR")
                        : ""}
                    </span>
                  </div>

                  <div className="flex items-center gap-4 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <Car className="h-3 w-3" />
                      {[
                        laudo.veiculo?.marca,
                        laudo.veiculo?.modelo,
                        laudo.veiculo?.anoFabricacao,
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    </span>
                    {laudo.rt && (
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {laudo.rt.nome}
                      </span>
                    )}
                    {laudo.parceiro && (
                      <span className="flex items-center gap-1">
                        <Building2 className="h-3 w-3" />
                        {laudo.parceiro.nomeFantasia ??
                          laudo.parceiro.razaoSocial}
                      </span>
                    )}
                    {laudo.tipo === "avaliacao" &&
                      laudo.veiculo?.valorFipe != null && (
                        <Badge className="text-xs bg-amber-100 text-amber-700">
                          FIPE {formatCurrency(Number(laudo.veiculo.valorFipe))}
                        </Badge>
                      )}
                    {laudo.tipo === "avaliacao" &&
                      laudo.valorDesejado != null && (
                        <Badge className="text-xs bg-blue-100 text-blue-700">
                          Desejado {formatCurrency(Number(laudo.valorDesejado))}
                        </Badge>
                      )}
                  </div>

                  {laudo.status === "finalizado" && laudo.conclusao && (
                    <div
                      className="flex items-center gap-2 pt-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Badge
                        className={`text-xs ${CONCLUSAO_COLORS[laudo.conclusao] ?? ""}`}
                      >
                        {CONCLUSAO_LABELS[laudo.conclusao] ?? laudo.conclusao}
                      </Badge>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-6 text-xs"
                        onClick={() =>
                          toast({
                            title:
                              "PDF disponível na tela de detalhes do laudo.",
                          })
                        }
                      >
                        <Download className="h-3 w-3 mr-1" />
                        Download PDF
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

      </div>
    </Shell>
  );
}
